import { list } from 'src/app/common/classes/request-builder';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { environment } from 'src/environments/environment';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { FitoLocalesModel } from 'src/app/models/tareas/fito-locales.model';
import {
    DropdownData,
    InputType,
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
    DropdownFilterContext,
} from '@fitogest/reactive-form-builder/interfaces';

export class FitoLocalesFormDefinition {
    public readonly hasClients = environment.features.hasClients;
    public readonly clienteName = environment.features.clienteName;
    public readonly fincaName = environment.features.fincaName;
    public readonly userCanSeeClientes = environment.features.userCanSeeClientes || StorageManager.getUser().rol === 'admin';

    public formType: FormType = FormType.CREATION;
    public model: Partial<FitoLocalesModel> = {};
    public formFields!: Form<FitoLocalesModel>;

    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);
    public productos: DropdownData<ProductosModel> = list<ProductosModel>(['...', null]);
    public clientes = list();

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<FitoLocalesModel>) {
        this.formType = +type;
        this.model = model;

        this.formFields = {
            name: 'fito-locales',
            header: 'Datos de la tarea fitosanitaria en locales',
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }

    private getFormSections(): FormSection<FitoLocalesModel>[] {
        const sections: FormSection<FitoLocalesModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 2,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getMainFields(): FormField<FitoLocalesModel>[] {
        const fields: FormField<FitoLocalesModel>[] = [
            {
                name: 'id_cliente',
                vinculatedFields: undefined,
                label: this.clienteName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.clientes,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                    },
                },
                width: 4,
                conditions: this.conditions({
                    isRequired: true,
                    isVisible: this.hasClients && this.userCanSeeClientes && this.formType === FormType.DUPLICATION,
                }),
                order: 1,
            },
            {
                name: 'id_finca',
                dependencies: [
                    {
                        source: 'id_cliente',
                        target: 'id_finca',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined,
                label: this.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.fincas,
                    defaultValue: this.model.id_finca ?? null,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it, context): boolean => this.filterFinca(it.value as FincasModel | null, context),
                    },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 2,
            },
            {
                name: 'fecha',
                vinculatedFields: undefined,
                label: 'Fecha',
                input: {
                    type: InputType.CALENDAR,
                    defaultValue: this.model.fecha ?? (this.formType === FormType.CREATION ? new Date() : null),
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 3,
            },
            {
                name: 'id_producto',
                vinculatedFields: undefined,
                label: 'Producto Fito',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.productos,
                    defaultValue: (this.model as Record<string, unknown>)['id_producto'] ?? null,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                    },
                },
                width: 4,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'local',
                vinculatedFields: undefined,
                label: 'Local',
                input: {
                    type: InputType.TEXT,
                    defaultValue: (this.model as Record<string, unknown>)['local'] ?? '',
                    isEditable: true,
                },
                width: 4,
                conditions: this.conditions(),
                order: 5,
            },
            {
                name: 'superficie_tratada',
                vinculatedFields: undefined,
                label: 'Superficie tratada',
                input: {
                    type: InputType.NUMERIC,
                    defaultValue: (this.model as Record<string, unknown>)['superficie_tratada'] ?? '',
                    isEditable: true,
                },
                width: 4,
                conditions: this.conditions(),
                order: 6,
            },
            {
                name: 'motivo',
                vinculatedFields: undefined,
                label: 'Motivo fitosanitario',
                input: {
                    type: InputType.TEXT,
                    defaultValue: (this.model as Record<string, unknown>)['motivo'] ?? '',
                    isEditable: true,
                },
                width: 4,
                conditions: this.conditions(),
                order: 7,
            },
        ];

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getHiddenFields(): FormField<FitoLocalesModel>[] {
        return [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id ?? null,
                    isEditable: false,
                },
                width: 1,
                conditions: this.conditions({ isVisible: true, isRememberedOnDuplicate: false }),
                order: 1,
            },
            {
                name: 'id_cliente',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id_cliente ?? this.client?.id ?? null,
                    isEditable: false,
                },
                width: 1,
                computed: {
                    dependsOn: ['id_finca', 'id_cliente'],
                    resolve: (context) => this.getPayloadClientId(context.getValue('id_finca'), context.getValue('id_cliente')),
                },
                conditions: this.conditions({
                    isVisible: !(this.hasClients && this.userCanSeeClientes && this.formType === FormType.DUPLICATION),
                }),
                order: 2,
            },
            {
                name: 'id_usuario',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id_usuario ?? this.user?.id ?? null,
                    isEditable: false,
                },
                width: 1,
                computed: {
                    dependsOn: ['id_finca'],
                    resolve: (context) => this.getPayloadUserId(context.getValue('id_finca')),
                },
                conditions: this.conditions({ isVisible: true }),
                order: 3,
            },
        ];
    }

    private getPayloadClientId(
        selectedFincaValue: unknown = this.model.id_finca,
        selectedClientValue: unknown = this.model.id_cliente,
    ): string {
        const selectedFinca = this.resolveSelectedDropdownEntity(this.fincas, selectedFincaValue);

        return String(
            selectedFinca?.id_cliente ??
            this.resolveEntityId(selectedClientValue) ??
            this.resolveEntityId(this.model.id_cliente) ??
            this.client?.id ??
            '',
        );
    }

    private getPayloadUserId(selectedFincaValue: unknown = this.model.id_finca): string {
        const selectedFinca = this.resolveSelectedDropdownEntity(this.fincas, selectedFincaValue);

        return String(
            selectedFinca?.id_usuario ??
            this.model.id_usuario ??
            this.user?.id ??
            '',
        );
    }

    private filterFinca(finca: FincasModel | null, context?: DropdownFilterContext<FitoLocalesModel>): boolean {
        if (!finca || !this.hasClients || this.formType === FormType.DUPLICATION) {
            return true;
        }

        if (this.client?.id) {
            return String(finca.id_cliente ?? '') === String(this.client.id);
        }

        const selectedClientId = this.resolveEntityId(context?.model.get('id_cliente')?.value ?? this.model.id_cliente);

        if (selectedClientId !== null) {
            return String(finca.id_cliente ?? '') === String(selectedClientId);
        }

        return true;
    }

    private resolveSelectedDropdownEntity<TEntity extends object>(
        dropdownData: DropdownData<TEntity>,
        selectedValue: unknown,
    ): TEntity | null {
        const selectedId = this.resolveEntityId(selectedValue);

        if (selectedId === null) {
            return null;
        }

        return dropdownData.values
            .map((option) => this.getDropdownOptionEntity<TEntity>(option))
            .find((entity): entity is TEntity => entity !== null && this.resolveEntityId(entity) === selectedId)
            ?? null;
    }

    private resolveEntityId(value: unknown): string | null {
        if (typeof value === 'object' && value !== null && 'id' in value) {
            const entityId = String((value as { id?: unknown }).id ?? '').trim();

            return entityId.length > 0 ? entityId : null;
        }

        const scalarValue = String(value ?? '').trim();

        return scalarValue.length > 0 ? scalarValue : null;
    }

    private getDropdownOptionEntity<TEntity extends object>(
        option: { readonly value: TEntity | null; readonly raw?: unknown },
    ): TEntity | null {
        if (typeof option.raw === 'object' && option.raw !== null) {
            return option.raw as unknown as TEntity;
        }

        if (typeof option.value === 'object' && option.value !== null) {
            return option.value as unknown as TEntity;
        }

        return null;
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
